import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { IssueTypeSchema } from './manufacturing.js';
import { pagedQuery, pagedResponseWith } from './pagination.js';
import { MessageSchema, QtyStringSchema, UnitPrecisionSchema } from './primitives.js';
const c = initContract();
/**
 * MFG-04 — MATERIAL CONSUMPTION. The permanent record of every part taken out
 * of stock and used.
 *
 * The word that matters most on this screen is IMMUTABLE, and this contract
 * shows it by what it does not contain: there is no update operation and no
 * delete operation, for any caller, at any permission, including the Owner
 * (§6). The only way a figure changes is `correct`, which POSTS A NEW VOUCHER
 * pointing back at the old one, and both stay for ever (BR-MFG-04-01/-02).
 *
 * ONE FORM, TWO DOORS. §5.5: "There is one form", reached from + New voucher
 * and from Correct. So `post` is one operation and `correctsVoucherId` is a
 * field on it, rather than two shapes that could drift apart.
 *
 * ONE PIECE OF DEAD CODE, DELIBERATELY NOT PORTED. §9 names it: the prototype
 * carried `quickMcRecord`, "a quick-consumption writer that would post a
 * ledger row with the warehouse hard-coded to the main warehouse, the issue
 * type hard-coded to Planned, and an empty note. No screen references it, it
 * is not behaviour, and it must not be built." There is no operation here that
 * writes a row with fewer than the full set of fields — a shortcut that
 * skipped the warehouse, the type and the note "would put back exactly the
 * fault this section rules out".
 */
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ConsumptionLineSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    /** Field 18: SIGNED. `+50` for an issue, `−8` for a return. */
    qty: z.string(),
    unitSymbol: z.string(),
    /**
     * `N-4` — the decimals THIS LINE's unit allows.
     * `consumption_voucher_lines.unit_id` is per line, so it follows the issue
     * as written rather than the item's stock unit (`BR-MD-03-04d`).
     */
    unitPrecision: UnitPrecisionSchema,
    /** Field 19 / BR-MFG-04-05: the average cost AT POSTING. Editable by nobody. */
    unitCost: z.string(),
    /** Field 20: `qty × unit cost`, 2 dp. Signed with the quantity. */
    value: z.string(),
    /** §5.5's [NOT IN PROTOTYPE] field, built. Null unless the item is tracked. */
    trackingType: z.enum(['none', 'serial', 'batch']),
    batchNo: z.string().nullable(),
    serialNos: z.string().array().nullable(),
})
    .strict();
const consumptionVoucherShape = z
    .object({
    id: z.uuid(),
    /** Field 14: MC-####. */
    code: z.string(),
    voucherNo: z.number().int(),
    /** Field 16: an underlined LINK. Blank when the voucher has no job. */
    workOrderId: z.uuid().nullable(),
    workOrderCode: z.string().nullable(),
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    /** Field 15: `2026-07-13 09:12`, company time zone. */
    issuedAt: z.string(),
    issueDate: z.string(),
    /** Field 26. §5.4's [recommended] Issued by column, built — a ledger that records who is worth more. */
    issuedBy: z.string(),
    issueType: IssueTypeSchema,
    note: z.string().nullable(),
    /** Field 14's amber sub-line: `↩ MC-1003`. */
    correctsVoucherId: z.uuid().nullable(),
    correctsCode: z.string().nullable(),
    /** True once some later voucher corrects this one — the chain, read forwards. */
    correctedByCodes: z.string().array(),
    journalEntryId: z.uuid().nullable(),
    /** Σ of the lines' values, 2 dp. */
    totalValue: z.string(),
    lines: ConsumptionLineSchema.array(),
    createdAt: z.string(),
})
    .strict();
/** §5.6 — one card per work order, one row per item. */
export const VarianceRowSchema = z
    .object({
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    /** Field 37: from the snapshot — `BOM qty incl. scrap × qty to produce`. */
    plannedQty: z.string(),
    /** Field 38: the NET of every ledger line for this job and item, corrections included. */
    actualQty: z.string(),
    varianceQty: z.string(),
    /**
     * Field 40: `variance ÷ planned × 100`, one decimal, signed. NULL when
     * planned is zero — BR-MFG-04-13: the screen shows `—` and it is not
     * clickable. "Division by zero must never reach the screen."
     */
    variancePct: z.string().nullable(),
    /** §5.6's colour band: 0–2 % grey, over 2 up to 5 amber, over 5 red. */
    band: z.enum(['normal', 'watch', 'investigate', 'none']),
})
    .strict();
export const VarianceCardSchema = z
    .object({
    workOrderId: z.uuid(),
    workOrderCode: z.string(),
    rows: VarianceRowSchema.array(),
})
    .strict();
/** §5.7 — aggregated by item, over its OWN date range, corrections netted. */
export const UsageRowSchema = z
    .object({
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    unitSymbol: z.string(),
    /**
     * `N-4`. This total is grouped BY ITEM, so it has one unit and `N-14`'s
     * "never added together" does not bite — that forbids a sum ACROSS items.
     */
    unitPrecision: UnitPrecisionSchema,
    totalQty: z.string(),
    value: z.string(),
})
    .strict();
const VoucherLineInputSchema = z
    .object({
    itemId: z.uuid(),
    /**
     * Field 29: "Must be greater than zero — the TYPE, not the sign typed
     * here, decides the direction." A caller cannot smuggle a return in as a
     * negative Planned issue.
     */
    qty: QtyStringSchema,
    /** §5.5's tracking field. Required when the item is tracked, refused when it is not. */
    batchNo: z.string().trim().max(40).nullable().optional(),
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
})
    .strict();
export const PostVoucherRequestSchema = z
    .object({
    /** Field 23: absent/null is "— No work order (indirect) —" (BR-MFG-04-21). */
    workOrderId: z.uuid().nullable().optional(),
    warehouseId: z.uuid(),
    /** Field 25: `YYYY-MM-DD HH:MM`, not in the future, in an open period. */
    issuedAt: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/),
    issuedBy: z.string().trim().min(1).max(80),
    issueType: IssueTypeSchema,
    note: z.string().trim().max(500).nullable().optional(),
    /**
     * Correction mode (§5.5). Setting it forces the type to Return-to-Stock —
     * "a correction is not a fifth type; it is a Return-to-Stock voucher that
     * carries a back-reference" — and makes the note required.
     */
    correctsVoucherId: z.uuid().nullable().optional(),
    lines: VoucherLineInputSchema.array().min(1).max(200),
})
    .strict();
/**
 * ── Why this one schema is declared and not inferred ────────────────────────
 * `ConsumptionVoucherSchema` answers 3 of this module's routes. Annotating it as
 * `z.ZodType<ConsumptionVoucherResponse>` writes the shape ONCE and lets every route
 * reference it by name, which keeps this module's emitted declaration a few
 * kilobytes instead of a hundred. Runtime validation is unchanged: the object
 * schema above is still what parses.
 */
export const ConsumptionVoucherSchema = consumptionVoucherShape;
export const materialConsumptionContract = c.router({
    ledger: {
        method: 'GET',
        path: '/api/manufacturing/consumption',
        query: pagedQuery({
            workOrderId: z.uuid().optional(),
            itemId: z.uuid().optional(),
            issueType: IssueTypeSchema.optional(),
            warehouseId: z.uuid().optional(),
            from: z.iso.date().optional(),
            to: z.iso.date().optional(),
        }),
        responses: {
            200: pagedResponseWith('vouchers', ConsumptionVoucherSchema, {
                /**
                 * Field 13 / BR-MFG-04-14: the total of the rows THE FILTERS
                 * CURRENTLY SHOW, not of everything ever posted — it changes
                 * whenever a filter changes.
                 *
                 * Paging does NOT narrow it further: it is an aggregate over the
                 * whole filtered set, like INV-06's summary cards. A total that
                 * meant "this page" would contradict the range printed beside it.
                 */
                totalValue: z.string(),
            }),
            401: MessageSchema,
            403: MessageSchema,
        },
        // BR-MFG-04-24: no filter is ever applied behind the user's back. An
        // absent parameter means ALL FOUR issue types, so a row a user posted is
        // always a row that user can find.
        summary: 'The one ledger (manufacturing.view), PAGED (NFR-42).',
    },
    get: {
        method: 'GET',
        path: '/api/manufacturing/consumption/:voucherId',
        pathParams: z.object({ voucherId: z.uuid() }).strict(),
        responses: {
            200: ConsumptionVoucherSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One voucher with its lines.',
    },
    post: {
        method: 'POST',
        path: '/api/manufacturing/consumption',
        body: PostVoucherRequestSchema,
        responses: {
            201: ConsumptionVoucherSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        // The verb is Post, not Save — §5.5 field 35, "accounting language for
        // an irreversible append". Writes one stock movement per line through
        // the ONE writer and one balanced journal entry per voucher
        // (BR-MFG-04-06). manufacturing.create; a CORRECTION additionally needs
        // manufacturing.approve (BR-MFG-04-23).
        summary: 'Post a voucher (manufacturing.create).',
    },
    variance: {
        method: 'GET',
        path: '/api/manufacturing/consumption/reports/variance',
        query: z.object({ workOrderId: z.uuid().optional() }).strict(),
        responses: {
            200: z.object({ cards: VarianceCardSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        // Planned from the snapshot, actual the NET of the ledger including
        // returns. Reading and drilling are all this tab does: no totals row, no
        // approve, no write-off, no filters.
        summary: '§5.6 Variance, one card per work order.',
    },
    usage: {
        method: 'GET',
        path: '/api/manufacturing/consumption/reports/usage',
        query: z.object({ from: z.iso.date(), to: z.iso.date() }).strict(),
        responses: {
            200: z.object({ rows: UsageRowSchema.array(), totalValue: z.string() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        // Grouped by item and by nothing else, over its OWN date range.
        // BR-MFG-04-15: this total and the ledger's for the same period must
        // never disagree.
        summary: '§5.7 Usage report.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=material-consumption.js.map